import React from "react";

/**
 * Generates a React component avatar with the given initial and background color
 * @param {string} initial - The initial to display (single character)
 * @param {string} backgroundColor - Background color (hex or CSS color)
 * @param {string} textColor - Text color (hex or CSS color)
 * @param {number} size - Size of the avatar in pixels (default: 130)
 * @returns {React.Component} - A React component that renders the avatar
 */
export function generateInitialAvatar(initial, backgroundColor = "#50bbe6", textColor = "#ffffff", size = 130) {
    return function InitialAvatar({ className = "", style = {}, ...props }) {
        return (
            <div
                className={`initial-avatar avatar ${className}`}
                style={{

                    backgroundColor,
                    fontSize: "13px",
                    height: size,
                    width: size,

                }}
                {...props}
            >
                {initial.toUpperCase()}
            </div>
        );
    };
}

const AVATAR_COLORS = [
    "#00A0B0",
    "#97AA0F",
    "#2980B9",
    "#F39C12",
    "#BD1550",
    "#E74C3C"
];

/**
 * Gets a consistent background color for an avatar based on any string identifier.
 * Same identifier always returns the same color. Used for workspace and user avatars.
 * @param {string} identifier - e.g. workspace name, user displayName, or email
 * @returns {string} - A hex color string
 */
export function getAvatarColorForIdentifier(identifier) {
    if (!identifier || typeof identifier !== "string") {
        return AVATAR_COLORS[0];
    }
    let hash = 0;
    for (let i = 0; i < identifier.length; i++) {
        const char = identifier.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    const index = Math.abs(hash) % AVATAR_COLORS.length;
    return AVATAR_COLORS[index];
}

/**
 * Shared workspace avatar display rules (WorkspaceInfoSection + nav UserMenu):
 * photo if set, else name initial + color. Never falls back to a user/owner avatar.
 *
 * @param {object} workspace - { id?, name?, photoURL?, workspacePhotoURL? }
 * @param {object} [options]
 * @param {string} [options.currentWorkspaceId]
 * @param {string|null} [options.currentWorkspacePhotoURL] - live workspace.photoURL when current
 * @returns {{ kind: "photo"|"initial"|"none", photoURL: string|null, initial: string, color: string, name: string }}
 */
export function resolveWorkspaceAvatar(workspace, {
    currentWorkspaceId,
    currentWorkspacePhotoURL
} = {}) {
    const name = workspace?.name || "";
    const isCurrentWorkspace = !!(
        workspace?.id &&
        currentWorkspaceId &&
        String(workspace.id) === String(currentWorkspaceId)
    );

    // Current workspace: trust live photoURL only (nav metadata may still carry owner photo).
    // Otherwise prefer workspacePhotoURL (metadata) then photoURL (workspace document).
    const photoURL = isCurrentWorkspace
        ? (currentWorkspacePhotoURL || null)
        : (workspace?.workspacePhotoURL || workspace?.photoURL || null);

    let kind = "none";
    if (photoURL) {
        kind = "photo";
    } else if (name) {
        kind = "initial";
    }
    return {
        kind,
        photoURL,
        initial: name ? name.charAt(0) : "?",
        color: getAvatarColorForIdentifier(name),
        name
    };
}

/**
 * Generates an avatar React component for a team workspace using the first initial of the workspace name
 * This function combines the workspace name's first character with a consistent color
 * to create a visually appealing avatar that represents the team workspace.
 *
 * @param {string} workspaceName - The name of the workspace
 * @param {number} size - Size of the avatar in pixels (default: 130)
 * @returns {React.Component} - A React component that renders the workspace avatar
 */
export function generateTeamWorkspaceAvatar(workspaceName, size = 130) {
    const initial = workspaceName?.charAt(0) || "?";
    const backgroundColor = getAvatarColorForIdentifier(workspaceName);

    return generateInitialAvatar(initial, backgroundColor, "#ffffff", size);
}
